import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Loader2, Search, SlidersHorizontal, ShieldCheck, ChevronDown, ChevronRight, AlertTriangle, ShieldAlert, Ban, Activity } from 'lucide-react';
import { toast } from 'react-toastify';
import { AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from 'recharts';
import baseURL from '../../utils/baseURL';
import useAxiosAuth from '../../hooks/useAxiosAuth';
import { FilterPanel, FilterSection, FilterOptionList, FilterPopover } from '../../components/common/FilterPanel';

const fmtTime = (d) => (d ? new Date(d).toLocaleString() : '—');
const roleColor = (r) => ({
  admin: 'bg-violet-100 text-violet-700',
  doctor: 'bg-[#4ABDBD]/15 text-[#2D8A8A]',
  patient: 'bg-blue-100 text-blue-700',
  anonymous: 'bg-gray-100 text-gray-500',
}[r] || 'bg-gray-100 text-gray-600');
const statusColor = (s) =>
  s >= 500 ? 'text-red-600' : s >= 400 ? 'text-amber-600' : s >= 300 ? 'text-sky-600' : 'text-emerald-600';

const ROLE_OPTS = [
  { value: 'admin', label: 'Admin' },
  { value: 'doctor', label: 'Doctor' },
  { value: 'patient', label: 'Patient' },
  { value: 'anonymous', label: 'Anonymous' },
];
const METHOD_OPTS = ['GET', 'POST', 'PUT', 'PATCH', 'DELETE'].map((m) => ({ value: m, label: m }));
const STATUS_OPTS = [
  { value: '2', label: 'Success (2xx)' },
  { value: '4', label: 'Client error (4xx)' },
  { value: '5', label: 'Server error (5xx)' },
];
const ACTION_OPTS = {
  login: [
    { value: 'login_success', label: 'Login success' },
    { value: 'login_failed', label: 'Login failed' },
    { value: 'logout', label: 'Logout' },
  ],
  appointment: [
    { value: 'appointment_booked', label: 'Booked' },
    { value: 'appointment_rescheduled', label: 'Rescheduled' },
    { value: 'appointment_cancelled', label: 'Cancelled' },
    { value: 'appointment_completed', label: 'Completed' },
    { value: 'appointment_noshow', label: 'No-show' },
  ],
  activity: [
    { value: 'create', label: 'Create' },
    { value: 'update', label: 'Update' },
    { value: 'delete', label: 'Delete' },
  ],
  socket: [
    { value: 'socket_connected', label: 'Connected' },
    { value: 'socket_disconnected', label: 'Disconnected' },
    { value: 'chat_message_sent', label: 'Chat message' },
    { value: 'chat_init', label: 'Chat init' },
    { value: 'chat_room_joined', label: 'Chat room joined' },
    { value: 'support_message_sent', label: 'Support message' },
    { value: 'support_ticket_opened', label: 'Support ticket opened' },
    { value: 'support_ticket_accepted', label: 'Support ticket accepted' },
    { value: 'support_ticket_assigned', label: 'Support ticket assigned' },
    { value: 'support_ticket_reassigned', label: 'Support ticket reassigned' },
    { value: 'support_ticket_closed', label: 'Support ticket closed' },
    { value: 'dashboard_subscribed', label: 'Dashboard subscribed' },
  ],
  security: [],
  api: [],
};
const EMPTY = { role: [], action: [], method: [], status: [], dateFrom: '', dateTo: '' };
const activeCount = (f) => f.role.length + f.action.length + f.method.length + f.status.length + (f.dateFrom ? 1 : 0) + (f.dateTo ? 1 : 0);

// Alerting dashboard shown above the Security Monitoring log table. Pulls the
// aggregate summary (failed-login trend, error totals, top offending IPs,
// suspected brute-force sources) from GET /audit/security/summary. Degrades
// silently if the endpoint isn't deployed yet (older backend).
function SecuritySummary({ axiosInstance }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    (async () => {
      setLoading(true);
      try {
        const res = await axiosInstance.get(`${baseURL}/audit/security/summary?days=7`);
        if (alive) setData(res.data || null);
      } catch {
        if (alive) setData(null); // endpoint missing/failed → hide the panel
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [axiosInstance]);

  if (loading) {
    return (
      <div className="bg-white rounded-2xl shadow-md p-6 mb-4 flex items-center justify-center">
        <Loader2 className="animate-spin text-[#4ABDBD]" size={22} />
      </div>
    );
  }
  if (!data || data.empty) return null;

  const t = data.totals || {};
  const trend = (data.failedLoginsOverTime || []).map((d) => ({ day: d.day?.slice(5) || d.day, count: d.count }));
  const cards = [
    { label: 'Failed logins (24h)', value: t.failed_logins ?? 0, icon: ShieldAlert, tone: 'text-amber-600 bg-amber-50' },
    { label: 'Client errors 4xx (24h)', value: t.errors4xx ?? 0, icon: AlertTriangle, tone: 'text-orange-600 bg-orange-50' },
    { label: 'Server errors 5xx (24h)', value: t.errors5xx ?? 0, icon: Activity, tone: 'text-red-600 bg-red-50' },
    { label: 'Offending IPs (7d)', value: data.uniqueOffendingIps ?? 0, icon: Ban, tone: 'text-violet-600 bg-violet-50' },
  ];

  return (
    <div className="mb-4 space-y-4" data-testid="security-summary">
      {/* KPI cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        {cards.map((c) => (
          <div key={c.label} className="bg-white rounded-2xl shadow-md p-4 flex items-center gap-3">
            <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${c.tone}`}><c.icon className="w-5 h-5" /></div>
            <div>
              <div className="text-2xl font-bold text-slate-800">{Number(c.value).toLocaleString()}</div>
              <div className="text-[11px] text-slate-400">{c.label}</div>
            </div>
          </div>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Failed-login trend */}
        <div className="bg-white rounded-2xl shadow-md p-4">
          <h3 className="text-sm font-semibold text-slate-700 mb-3">Failed logins — last {data.days} days</h3>
          {trend.length === 0 ? (
            <p className="text-xs text-slate-400 py-10 text-center">No failed logins in this window.</p>
          ) : (
            <ResponsiveContainer width="100%" height={180}>
              <AreaChart data={trend} margin={{ top: 5, right: 8, left: -20, bottom: 0 }}>
                <defs>
                  <linearGradient id="flFill" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#f59e0b" stopOpacity={0.4} />
                    <stop offset="100%" stopColor="#f59e0b" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                <XAxis dataKey="day" tick={{ fontSize: 11, fill: '#94a3b8' }} />
                <YAxis allowDecimals={false} tick={{ fontSize: 11, fill: '#94a3b8' }} />
                <Tooltip />
                <Area type="monotone" dataKey="count" stroke="#f59e0b" strokeWidth={2} fill="url(#flFill)" />
              </AreaChart>
            </ResponsiveContainer>
          )}
        </div>

        {/* Top offending IPs + brute-force alerts */}
        <div className="bg-white rounded-2xl shadow-md p-4">
          <h3 className="text-sm font-semibold text-slate-700 mb-3">Top offending IPs (7 days)</h3>
          {(data.bruteForce || []).length > 0 && (
            <div className="mb-3 p-2.5 rounded-xl bg-red-50 border border-red-100 flex items-start gap-2">
              <AlertTriangle className="w-4 h-4 text-red-500 mt-0.5 shrink-0" />
              <div className="text-xs text-red-700">
                <span className="font-semibold">Suspected brute-force</span> — {data.bruteForce.length} IP(s) over {data.bruteThreshold} failed logins in 24h:
                <span className="font-mono"> {data.bruteForce.slice(0, 5).map((b) => `${b.ip} (${b.failed_logins})`).join(', ')}</span>
              </div>
            </div>
          )}
          {(data.topOffendingIps || []).length === 0 ? (
            <p className="text-xs text-slate-400 py-8 text-center">No offending IPs in this window.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead>
                  <tr className="text-left text-slate-400 border-b border-gray-100">
                    <th className="py-1.5 pr-2 font-semibold">IP</th>
                    <th className="py-1.5 pr-2 font-semibold text-right">Failed logins</th>
                    <th className="py-1.5 pr-2 font-semibold text-right">Errors</th>
                    <th className="py-1.5 font-semibold text-right">Total</th>
                  </tr>
                </thead>
                <tbody>
                  {data.topOffendingIps.map((r) => (
                    <tr key={r.ip} className="border-b border-gray-50">
                      <td className="py-1.5 pr-2 font-mono text-slate-600">{r.ip}</td>
                      <td className="py-1.5 pr-2 text-right text-amber-600 font-semibold">{r.failed_logins}</td>
                      <td className="py-1.5 pr-2 text-right text-slate-500">{r.errors}</td>
                      <td className="py-1.5 text-right font-semibold text-slate-700">{r.total}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// Generic Audit & Security log viewer. `category` scopes GET /api/audit/logs.
// Shows the resolved actor (name · id · role), the same Search + Filter-popover UI
// as the Doctors page, pagination, and an expandable row detail.
export default function AuditLogs({ category, title, description }) {
  const axiosInstance = useAxiosAuth();
  // Categories are resolved server-side now:
  //   activity = real category='activity' rows (create/update/delete with field diffs)
  //   security = union of real 'security' rows + API 4xx/5xx errors
  // API + Security rows carry method/status/path columns; activity rows don't.
  const isApi = category === 'api' || category === 'security';
  const showApiFilters = category === 'api'; // raw method/status pickers only on the API page
  const isSecurity = category === 'security';
  const actionOptions = ACTION_OPTS[category] || [];

  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [query, setQuery] = useState('');
  const [filters, setFilters] = useState(EMPTY);
  const [tempFilters, setTempFilters] = useState(EMPTY);
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [expanded, setExpanded] = useState(null);
  const filterBtnRef = useRef(null);
  const filterRef = useRef(null);

  useEffect(() => { setPage(1); }, [category, query, filters, pageSize]);
  useEffect(() => { if (isFilterOpen) setTempFilters(filters); }, [isFilterOpen]); // eslint-disable-line react-hooks/exhaustive-deps

  // Close the popover on outside click.
  useEffect(() => {
    const onDown = (e) => {
      if (isFilterOpen && filterRef.current && !filterRef.current.contains(e.target) && filterBtnRef.current && !filterBtnRef.current.contains(e.target)) {
        setTempFilters(filters);
        setIsFilterOpen(false);
      }
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [isFilterOpen, filters]);

  useEffect(() => {
    let alive = true;
    (async () => {
      setLoading(true);
      try {
        const params = new URLSearchParams({ category, page: String(page), pageSize: String(pageSize) });
        if (query) params.append('search', query);
        if (filters.role.length) params.append('role', filters.role.join(','));
        if (filters.action.length) params.append('action', filters.action.join(','));
        if (filters.method.length) params.append('method', filters.method.join(','));
        if (filters.status.length) params.append('statusClass', filters.status.join(','));
        if (filters.dateFrom) params.append('startDate', new Date(filters.dateFrom).toISOString());
        if (filters.dateTo) { const d = new Date(filters.dateTo); d.setHours(23, 59, 59, 999); params.append('endDate', d.toISOString()); }
        const res = await axiosInstance.get(`${baseURL}/audit/logs?${params.toString()}`);
        if (alive) { setRows(res.data?.logs || []); setTotal(res.data?.total || 0); }
      } catch {
        if (alive) { setRows([]); setTotal(0); toast.warn('Failed to load logs'); }
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [category, page, pageSize, query, filters]);

  const pages = useMemo(() => Math.max(1, Math.ceil(total / pageSize)), [total, pageSize]);
  const nActive = activeCount(filters);

  return (
    <div className="min-h-full w-full p-2 sm:p-4" data-testid={`audit-page-${category}`}>
      <h1 className="text-2xl sm:text-3xl font-bold bg-gradient-to-r from-[#4ABDBD] to-[#3AA8A8] bg-clip-text text-transparent flex items-center gap-2">
        <ShieldCheck className="w-6 h-6 text-[#4ABDBD]" /> {title}
      </h1>
      <p className="text-sm text-slate-500 mt-1 mb-4">{description}</p>

      {/* Alerting dashboard — Security Monitoring only */}
      {isSecurity && <SecuritySummary axiosInstance={axiosInstance} />}

      {/* Search + Filter (Doctors-page style) */}
      <div className="flex flex-wrap items-center gap-2 mb-4">
        <div className="relative flex-1 min-w-[220px] max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={16} />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && setQuery(search.trim())}
            placeholder="Search name / id / path / action…"
            data-testid="audit-search"
            className="w-full pl-9 pr-3 py-2.5 text-sm border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#4ABDBD]"
          />
        </div>
        <button type="button" onClick={() => setQuery(search.trim())}
          className="px-3 py-2.5 text-sm rounded-xl bg-[#4ABDBD] text-white font-semibold hover:bg-[#3AA8A8]">Search</button>

        <div className="relative">
          <button
            ref={filterBtnRef}
            type="button"
            data-testid="audit-filter-button"
            onClick={() => setIsFilterOpen((o) => !o)}
            className="relative flex items-center gap-2 px-3 py-2.5 bg-gradient-to-r from-[#4ABDBD] via-[#3AA8A8] to-[#2D8A8A] text-white rounded-xl shadow hover:shadow-lg transition-all font-semibold text-sm"
          >
            <SlidersHorizontal className="w-5 h-5" />
            {nActive > 0 && <span className="absolute -top-1.5 -right-1.5 bg-white text-[#2D8A8A] text-[10px] font-bold w-5 h-5 rounded-full flex items-center justify-center shadow">{nActive}</span>}
          </button>
          {isFilterOpen && (
            <FilterPopover ref={filterRef}>
              <FilterPanel
                onClear={() => { setTempFilters(EMPTY); setFilters(EMPTY); setIsFilterOpen(false); }}
                onApply={() => { setFilters(tempFilters); setIsFilterOpen(false); }}
                clearLabel="Clear"
                applyLabel="Apply"
              >
                <FilterSection title="Role" isActive={tempFilters.role.length > 0}>
                  <FilterOptionList multiple value={tempFilters.role} onChange={(v) => setTempFilters({ ...tempFilters, role: v })} options={ROLE_OPTS} />
                </FilterSection>

                {actionOptions.length > 0 && (
                  <FilterSection title="Event" isActive={tempFilters.action.length > 0}>
                    <FilterOptionList multiple value={tempFilters.action} onChange={(v) => setTempFilters({ ...tempFilters, action: v })} options={actionOptions} />
                  </FilterSection>
                )}

                {showApiFilters && (
                  <>
                    <FilterSection title="Method" isActive={tempFilters.method.length > 0}>
                      <FilterOptionList multiple value={tempFilters.method} onChange={(v) => setTempFilters({ ...tempFilters, method: v })} options={METHOD_OPTS} />
                    </FilterSection>
                    <FilterSection title="Status" isActive={tempFilters.status.length > 0}>
                      <FilterOptionList multiple value={tempFilters.status} onChange={(v) => setTempFilters({ ...tempFilters, status: v })} options={STATUS_OPTS} />
                    </FilterSection>
                  </>
                )}

                <FilterSection title="Date range" isActive={!!(tempFilters.dateFrom || tempFilters.dateTo)}>
                  <div className="flex flex-col gap-2 px-1 py-1">
                    <label className="text-xs text-gray-500">From
                      <input type="date" value={tempFilters.dateFrom} onChange={(e) => setTempFilters({ ...tempFilters, dateFrom: e.target.value })}
                        className="mt-1 w-full px-2 py-1.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#4ABDBD]" />
                    </label>
                    <label className="text-xs text-gray-500">To
                      <input type="date" value={tempFilters.dateTo} onChange={(e) => setTempFilters({ ...tempFilters, dateTo: e.target.value })}
                        className="mt-1 w-full px-2 py-1.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#4ABDBD]" />
                    </label>
                  </div>
                </FilterSection>
              </FilterPanel>
            </FilterPopover>
          )}
        </div>

        <div className="ml-auto flex items-center gap-2 text-xs text-slate-400">
          <span>{total.toLocaleString()} entries</span>
          <select value={pageSize} onChange={(e) => setPageSize(Number(e.target.value))}
            className="px-2 py-1.5 border border-gray-300 rounded-lg text-xs focus:outline-none focus:ring-2 focus:ring-[#4ABDBD]">
            {[25, 50, 100].map((n) => <option key={n} value={n}>{n} / page</option>)}
          </select>
        </div>
      </div>

      <div className="bg-white rounded-2xl shadow-md p-4 sm:p-5">
        {loading ? (
          <div className="flex items-center justify-center py-16" data-testid="audit-loading"><Loader2 className="animate-spin text-[#4ABDBD]" size={30} /></div>
        ) : rows.length === 0 ? (
          <p className="text-sm text-slate-500 py-10 text-center" data-testid="audit-empty">No log entries for this view.</p>
        ) : (
          <>
            <div className="overflow-x-auto custom-scrollbar">
              <table className="w-full text-sm whitespace-nowrap">
                <thead>
                  <tr className="text-left text-xs text-slate-400 border-b border-gray-100">
                    <th className="py-2 pr-2 w-6"></th>
                    <th className="py-2 pr-3 font-semibold">Time</th>
                    <th className="py-2 pr-3 font-semibold">User</th>
                    <th className="py-2 pr-3 font-semibold">Action</th>
                    {isApi && <th className="py-2 pr-3 font-semibold">Path</th>}
                    {isApi && <th className="py-2 pr-3 font-semibold text-right">Status</th>}
                    {isApi && <th className="py-2 pr-3 font-semibold text-right">ms</th>}
                    <th className="py-2 font-semibold">IP</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r) => {
                    const open = expanded === r.id;
                    return (
                      <React.Fragment key={r.id}>
                        <tr className="border-b border-gray-50 hover:bg-gray-50 cursor-pointer" data-testid={`audit-row-${r.id}`} onClick={() => setExpanded(open ? null : r.id)}>
                          <td className="py-2.5 pr-2 text-gray-400">{open ? <ChevronDown size={15} /> : <ChevronRight size={15} />}</td>
                          <td className="py-2.5 pr-3 text-gray-600">{fmtTime(r.occurredAt)}</td>
                          <td className="py-2.5 pr-3">
                            <div className="flex items-center gap-2">
                              <span className="text-gray-800 font-medium">{r.actorName || (r.actorRole === 'anonymous' ? 'Anonymous' : (r.actorBusinessId || '—'))}</span>
                              <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full ${roleColor(r.actorRole)}`}>{r.actorRole || '—'}</span>
                            </div>
                            {r.actorBusinessId && <div className="text-[11px] text-slate-400 font-mono">{r.actorBusinessId}</div>}
                          </td>
                          <td className="py-2.5 pr-3 text-gray-700"><div className="max-w-[220px] truncate" title={r.action || ''}>{r.action || '—'}</div></td>
                          {isApi && <td className="py-2.5 pr-3"><div className="max-w-[220px] truncate text-gray-500" title={r.path || ''}>{r.path || '—'}</div></td>}
                          {isApi && <td className={`py-2.5 pr-3 text-right font-semibold ${statusColor(r.statusCode)}`}>{r.statusCode ?? '—'}</td>}
                          {isApi && <td className="py-2.5 pr-3 text-right text-gray-500">{r.durationMs ?? '—'}</td>}
                          <td className="py-2.5 text-gray-500 font-mono text-xs">{r.ip || '—'}</td>
                        </tr>
                        {open && (
                          <tr className="bg-gray-50/70" data-testid={`audit-detail-${r.id}`}>
                            {/* whitespace-normal: the parent table is whitespace-nowrap; without
                                this the long Device/user-agent string overflows instead of wrapping. */}
                            <td colSpan={isApi ? 8 : 5} className="px-4 py-3 whitespace-normal break-words">
                              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-6 gap-y-1.5 text-xs">
                                <Detail k="User name" v={r.actorName} />
                                <Detail k="Business id" v={r.actorBusinessId} mono />
                                <Detail k="Email" v={r.actorEmail} />
                                <Detail k="Role" v={r.actorRole} />
                                <Detail k="Action" v={r.action} />
                                <Detail k="Method" v={r.method} />
                                <Detail k="Path" v={r.path} mono />
                                <Detail k="Status" v={r.statusCode} />
                                <Detail k="Duration" v={r.durationMs != null ? `${r.durationMs} ms` : null} />
                                <Detail k="IP" v={r.ip} mono />
                                <Detail k="Entity" v={r.entity && r.entityId ? `${r.entity} · ${r.entityId}` : r.entity} />
                                <Detail k="Device" v={r.userAgent} />
                                {r.meta?.changes && Object.keys(r.meta.changes).length > 0 && (
                                  <div className="sm:col-span-2 lg:col-span-3 mt-1">
                                    <span className="text-slate-400">Changed fields:</span>
                                    <div className="mt-1 overflow-x-auto">
                                      <table className="text-[11px] border-collapse">
                                        <thead>
                                          <tr className="text-slate-400 text-left">
                                            <th className="pr-4 pb-1 font-semibold">Field</th>
                                            <th className="pr-4 pb-1 font-semibold">Old</th>
                                            <th className="pb-1 font-semibold">New</th>
                                          </tr>
                                        </thead>
                                        <tbody>
                                          {Object.entries(r.meta.changes).map(([field, ch]) => (
                                            <tr key={field} className="align-top">
                                              <td className="pr-4 py-0.5 font-mono text-slate-600">{field}</td>
                                              <td className="pr-4 py-0.5 font-mono text-rose-600 break-all max-w-[220px]">{ch.old ?? '—'}</td>
                                              <td className="py-0.5 font-mono text-emerald-600 break-all max-w-[220px]">{ch.new ?? '—'}</td>
                                            </tr>
                                          ))}
                                        </tbody>
                                      </table>
                                    </div>
                                  </div>
                                )}
                                {r.meta && !r.meta.changes && <div className="sm:col-span-2 lg:col-span-3"><span className="text-slate-400">Details:</span> <span className="font-mono text-gray-700 break-all">{JSON.stringify(r.meta)}</span></div>}
                              </div>
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>
            <div className="flex items-center justify-between mt-4 text-sm">
              <span className="text-slate-400">Page {page} of {pages}</span>
              <div className="flex gap-2">
                <button type="button" disabled={page <= 1} onClick={() => setPage((p) => Math.max(1, p - 1))}
                  className={`px-3 py-1.5 rounded-lg border ${page <= 1 ? 'text-gray-300 border-gray-100' : 'text-gray-600 border-gray-300 hover:bg-gray-50'}`}>Prev</button>
                <button type="button" disabled={page >= pages} onClick={() => setPage((p) => Math.min(pages, p + 1))}
                  className={`px-3 py-1.5 rounded-lg border ${page >= pages ? 'text-gray-300 border-gray-100' : 'text-gray-600 border-gray-300 hover:bg-gray-50'}`}>Next</button>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

const Detail = ({ k, v, mono }) => (
  <div className="min-w-0 break-words">
    <span className="text-slate-400">{k}: </span>
    <span className={`text-gray-700 break-all ${mono ? 'font-mono' : ''}`}>{v || '—'}</span>
  </div>
);
